'use client';

import * as React from 'react';
import { cn } from '../lib/cn';
import { Label } from './Label';

/**
 * FormField — composition of label + control + helper/error text.
 *
 * Wires aria-describedby automatically: the helper/error <p> gets a generated
 * id and the rendered child receives it. Pair with React Hook Form's Controller
 * by passing `error` from formState.errors[name]?.message.
 *
 * a11y:
 *   - aria-describedby connects helper text + error text
 *   - aria-invalid set on child input via `error` prop
 *   - Required asterisk in label is visual + sr-only "required"
 */

export interface FormFieldProps {
  label?: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  helper?: React.ReactNode;
  error?: React.ReactNode;
  optional?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function FormField({
  label,
  htmlFor,
  required,
  helper,
  error,
  optional,
  className,
  children,
}: FormFieldProps) {
  const reactId = React.useId();
  const fieldId = htmlFor ?? reactId;
  const helperId = helper ? `${fieldId}-helper` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <div className="flex items-baseline justify-between gap-2">
          <Label htmlFor={fieldId} required={!!required}>
            {label}
          </Label>
          {optional ? (
            <span className="text-xs text-text-muted">Optional</span>
          ) : null}
        </div>
      ) : null}

      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
            id: fieldId,
            'aria-describedby': describedBy,
            'aria-invalid': error ? true : undefined,
          })
        : children}

      {helper && !error ? (
        <p id={helperId} className="text-xs text-text-muted">
          {helper}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-xs text-error-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
